import { Section, SectionHeading } from "@/components/Section";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Terms() {
  return (
    <div data-testid="terms-page">
      <Section className="pt-28 pb-12">
        <SectionHeading
          eyebrow="Legal"
          title="Terms of Service"
          intro="By accessing or using the Nodera Health platform, you agree to these Terms of Service. If you do not agree, do not use the service."
          align="center"
        />
        <div className="mt-2 text-center text-xs uppercase tracking-[0.18em] font-semibold text-slate-400">
          Effective February 26, 2026
        </div>
      </Section>

      <Section className="pt-0 pb-24">
        <article className="mx-auto max-w-3xl prose prose-slate prose-headings:font-display prose-headings:text-slate-900 prose-h2:mt-12 prose-h2:mb-3 prose-h2:text-xl prose-p:text-slate-700 prose-li:text-slate-700">
          <h2>1. The service</h2>
          <p>
            Nodera Health, Inc. (&quot;Nodera&quot;) provides operational infrastructure for
            cash-pay healthcare clinics — vendor network discovery, program activation, workflow
            automation, training, and marketing tooling. We are not a clinical-care provider; we do
            not provide medical advice, prescribing, diagnosis interpretation, or patient-care
            management. Clinical responsibility remains with the clinic and its licensed
            practitioners.
          </p>

          <h2>2. Eligibility</h2>
          <p>
            You may use Nodera only if you are at least 18, legally able to enter into binding
            contracts, and authorized to act on behalf of the clinic, vendor, or organization you
            represent.
          </p>

          <h2>3. Accounts &amp; access</h2>
          <ul>
            <li>You are responsible for safeguarding your login credentials.</li>
            <li>You must notify us promptly at <a href="mailto:info@noderahealth.com" className="text-blue-700 hover:underline">info@noderahealth.com</a> of any unauthorized use.</li>
            <li>Access requests for the clinic portal are reviewed and approved by Nodera; we may decline access at our discretion.</li>
          </ul>

          <h2>4. Subscription &amp; payment</h2>
          <p>
            Paid subscriptions auto-renew monthly or annually based on your selected term.
            Cancellations take effect at the end of the current billing period. Refunds are not
            issued for partial periods unless required by law. Pricing and tier features may change;
            we will give at least 30 days&apos; notice before any increase to your active plan.
          </p>

          <h2>5. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the platform to violate any law, including consumer-protection or healthcare-marketing regulations applicable to your jurisdiction.</li>
            <li>Upload patient PHI directly into the platform unless explicitly authorized by your BAA with Nodera.</li>
            <li>Attempt to bypass access controls, scrape data outside the public API, or interfere with the operation of the service.</li>
            <li>Resell, sublicense, or white-label the platform without a written agreement (this is available on the Scale tier).</li>
          </ul>

          <h2>6. Vendor &amp; clinic relationships</h2>
          <p>
            Vendors listed in the Nodera Health network operate independently. Nodera vets vendors
            for basic compliance attestation but does <strong>not</strong> guarantee vendor
            performance, pricing, or outcomes. Clinics negotiate and contract directly with vendors
            through the activation flow.
          </p>

          <h2>7. Intellectual property</h2>
          <p>
            Nodera retains all rights to the platform, templates, workflows, and marketing assets we
            create. You retain ownership of clinic-specific content you upload (logos, custom
            playbooks, branding). By uploading content, you grant Nodera a limited license to
            display, render, and process that content solely to provide the service.
          </p>

          <h2>8. AI-generated content</h2>
          <p>
            Some platform features (vendor summaries, intelligence briefings, marketing copy
            suggestions) are generated by AI. Outputs are best-effort and may contain inaccuracies;
            review before use, especially in regulated marketing contexts.
          </p>

          <h2>9. Termination</h2>
          <p>
            You may cancel at any time from your billing settings. We may suspend or terminate
            access for material breach of these Terms (e.g. acceptable-use violations) with 7
            days&apos; notice unless the breach poses immediate harm. Upon termination, you have 30
            days to export your data; after that, data is deleted per our{" "}
            <Link to="/privacy" className="text-blue-700 hover:underline">
              Privacy Policy
            </Link>
            .
          </p>

          <h2>10. Disclaimers</h2>
          <p>
            The service is provided <strong>&quot;as is&quot;</strong> without warranty of any kind,
            express or implied. We do not warrant that the service will be uninterrupted or
            error-free.
          </p>

          <h2>11. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, Nodera&apos;s total liability for any claim
            arising out of or related to the service is limited to the amount you paid us in the
            12 months immediately preceding the claim.
          </p>

          <h2>12. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Delaware, USA, without regard to
            conflict-of-law principles.
          </p>

          <h2>13. Changes</h2>
          <p>
            We may update these Terms from time to time. Material changes will be announced via
            email to account admins at least 30 days before they take effect.
          </p>

          <h2>14. Contact</h2>
          <p>
            Questions about these Terms:{" "}
            <a href="mailto:info@noderahealth.com" className="text-blue-700 hover:underline">
              info@noderahealth.com
            </a>
            .
          </p>

          <hr className="my-10 border-slate-200" />
          <p className="text-sm">
            <Link
              to="/"
              data-testid="terms-back-home"
              className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Nodera Health
            </Link>
          </p>
        </article>
      </Section>
    </div>
  );
}
